<script setup lang="ts">
import {
  compactXp,
  displayFrom,
  hexAlpha,
  levelBenefits,
  levelTiers,
  mixColor,
  xpForLevelIn,
} from '@/utils/levels';

const props = defineProps<{
  /** Nível do jogador — define o que aparece feito, atual e bloqueado. */
  level: number;
  playerTierIndex: number;
  /** Régua de XP indexada pelo próprio nível (`table[n]` = nível n). */
  levelXpTable: number[];
}>();

const emit = defineEmits<{ selectTier: [index: number] }>();

type RowState = 'done' | 'current' | 'next';

function rowState(n: number): RowState {
  if (n < props.level) return 'done';

  return n === props.level ? 'current' : 'next';
}

/**
 * Um cartão por elo, cada um com a trilha dos seus níveis.
 *
 * A trilha mostra só progresso (nível, XP, estado). Os prêmios que cada degrau
 * abre saíram da vitrine de propósito: a tela não anuncia bônus concreto.
 */
const cards = computed(() => {
  // Artes resolvidas pelo CDN aqui dentro: `levelTiers()`/`levelBenefits()`
  // leem `useRuntimeConfig()`, que só responde no contexto Nuxt.
  const benefits = levelBenefits();

  return levelTiers().map((tier, index) => {
    // Pendentes fora dos chips: não se promete o que ainda não existe.
    const newBenefits = benefits.filter(
      (b) => b.from >= tier.from && b.from <= tier.to && !b.pending,
    );

    const state: RowState =
      index < props.playerTierIndex
        ? 'done'
        : index === props.playerTierIndex
          ? 'current'
          : 'next';

    const levels = [];

    // `displayFrom`: a trilha do Bronze começa no nível 1 — o nível 0 é o
    // estado "sem elo" e não é degrau de elo nenhum na vitrine.
    for (let n = displayFrom(tier); n <= tier.to; n++) {
      const st = rowState(n);

      levels.push({
        n,
        label: n >= 30 ? `VIP ${n - 30}` : `Nível ${n}`,
        xp: `${compactXp(xpForLevelIn(props.levelXpTable, n))} XP`,
        state: st,
        isLast: n === tier.to,
        status:
          st === 'current'
            ? 'Nível atual'
            : st === 'done'
              ? 'Feito'
              : n === props.level + 1
                ? 'Próximo'
                : 'Bloqueado',
        statusFg:
          st === 'done'
            ? '#3FDD8E'
            : st === 'current'
              ? '#fff'
              : n === props.level + 1
                ? mixColor(tier.color, '#ffffff', 0.7)
                : 'rgba(255,255,255,.56)',
      });
    }

    return {
      tier,
      index,
      state,
      range: tier.isVip ? tier.name : `Nível ${displayFrom(tier)}–${tier.to}`,
      // VIP 4 é o degrau do gerente: o que ele abre é combinado direto com o
      // jogador, então a legenda não conta prêmio. Os outros VIPs sem prêmio
      // novo mostram o maior benefício do elo em vez de um genérico.
      count:
        tier.key === 'v4'
          ? 'Definido com seu gerente'
          : newBenefits.length
            ? `${newBenefits.length} ${newBenefits.length === 1 ? 'novo prêmio' : 'novos prêmios'}`
            : tier.isVip
              ? tier.topBenefit
              : 'Sem prêmio novo',
      tagText:
        state === 'done'
          ? 'Feito'
          : state === 'current'
            ? 'Você está aqui'
            : 'Bloqueado',
      levels,
    };
  });
});
</script>

<template>
  <div class="flex flex-col gap-3.5 px-[9px] pb-9 pt-4 lg:px-[13px]">
    <span
      class="text-[17px] font-bold leading-[22px] tracking-[-0.015em] text-white"
    >
      Todos os níveis
    </span>

    <button
      v-for="card in cards"
      :key="card.tier.key"
      class="overflow-hidden rounded-[14px] text-left"
      :style="{
        boxShadow:
          card.state === 'current'
            ? `inset 0 0 0 1px ${hexAlpha(card.tier.color, 0.5)}`
            : 'inset 0 0 0 1px rgba(255,255,255,.07)',
      }"
      type="button"
      @click="emit('selectTier', card.index)"
    >
      <!-- Cabeçalho: badge, nome, faixa de níveis e selo de estado -->
      <div
        class="flex items-center gap-[11px] p-3"
        :style="{
          background:
            card.state === 'next'
              ? `linear-gradient(100deg,${hexAlpha(card.tier.color, 0.07)},rgba(255,255,255,.02))`
              : `linear-gradient(100deg,${mixColor(card.tier.color, '#070E1E', 0.52)},rgba(7,14,30,.35))`,
        }"
      >
        <span
          class="h-10 w-10 flex-none bg-contain bg-center bg-no-repeat"
          :style="{
            backgroundImage: `url(${card.tier.badge})`,
            filter:
              card.state === 'next'
                ? 'grayscale(.7) brightness(.8)'
                : `drop-shadow(0 0 8px ${hexAlpha(card.tier.color, 0.6)})`,
          }"
        ></span>

        <div class="flex min-w-0 flex-1 flex-col gap-0.5">
          <span
            class="flex items-center gap-[7px] whitespace-nowrap text-[15.5px] font-bold leading-5 tracking-[-0.015em]"
            :style="{
              color: card.state === 'next' ? 'rgba(255,255,255,.76)' : '#fff',
            }"
          >
            {{ card.tier.name }}
            <NuxtIcon
              v-if="card.state === 'next'"
              class="flex-none text-[13px] text-white/[0.55]"
              filled
              name="mini-games/locked"
            />
          </span>
          <span
            class="text-[10.5px] font-normal leading-[14px] text-white/[0.62]"
          >
            {{ card.range }} · {{ card.count }}
          </span>
        </div>

        <span
          class="flex flex-none items-center gap-[5px] whitespace-nowrap rounded-full px-2.5 py-[5px] text-[9.5px] font-bold uppercase leading-3 tracking-[0.08em]"
          :style="{
            background:
              card.state === 'current'
                ? hexAlpha(card.tier.color, 0.9)
                : card.state === 'done'
                  ? 'rgba(63,221,142,.14)'
                  : 'rgba(255,255,255,.08)',
            color:
              card.state === 'current'
                ? '#0A0F1E'
                : card.state === 'done'
                  ? '#3FDD8E'
                  : 'rgba(255,255,255,.72)',
          }"
        >
          {{ card.tagText }}
        </span>
      </div>

      <!-- Trilha dos níveis do elo -->
      <div
        class="px-3 pb-[3px] pt-[11px]"
        :style="{
          background:
            card.state === 'next'
              ? `linear-gradient(180deg,${hexAlpha(card.tier.color, 0.04)},rgba(255,255,255,.015))`
              : `linear-gradient(180deg,${hexAlpha(card.tier.color, 0.09)},${hexAlpha(card.tier.color, 0.03)})`,
        }"
      >
        <div
          v-for="row in card.levels"
          :key="row.n"
          class="relative flex gap-[11px] overflow-hidden rounded-[10px]"
          :class="
            row.state === 'current'
              ? '-mx-[9px] mb-1 -mt-1 px-[9px] pb-[3px] pt-[7px]'
              : ''
          "
          :style="{
            background:
              row.state === 'current'
                ? `linear-gradient(100deg,${hexAlpha(card.tier.color, 0.26)},${hexAlpha(card.tier.color, 0.1)})`
                : 'transparent',
            boxShadow:
              row.state === 'current'
                ? `0 10px 26px -14px ${hexAlpha(card.tier.color, 0.9)}`
                : 'none',
          }"
        >
          <span
            v-if="row.state === 'current'"
            aria-hidden="true"
            class="levels-neon pointer-events-none absolute inset-0 rounded-[10px]"
            :style="{
              boxShadow: `inset 0 0 0 1.5px ${hexAlpha(card.tier.color, 0.95)},0 0 16px ${hexAlpha(card.tier.color, 0.55)}`,
            }"
          ></span>

          <!-- Trilho vertical + ponto do nível -->
          <span class="relative flex w-[11px] flex-none justify-center">
            <span
              class="absolute bottom-0 top-1.5 w-0.5 rounded-sm"
              :style="{
                background: row.isLast
                  ? 'transparent'
                  : row.state === 'done'
                    ? hexAlpha(card.tier.color, 0.5)
                    : 'rgba(255,255,255,.1)',
              }"
            ></span>
            <span
              class="relative mt-[3px] h-[9px] w-[9px] flex-none rounded-full"
              :style="{
                background:
                  row.state === 'next'
                    ? 'rgba(255,255,255,.22)'
                    : card.tier.color,
                boxShadow: `0 0 0 3px ${
                  row.state === 'current'
                    ? hexAlpha(card.tier.color, 0.28)
                    : 'transparent'
                }`,
              }"
            ></span>
          </span>

          <div class="min-w-0 flex-1 pb-[13px]">
            <div class="flex items-baseline gap-[9px]">
              <span
                class="flex-none text-[12.5px] font-bold leading-4 tracking-[-0.01em]"
                :style="{
                  color: row.state === 'next' ? 'rgba(255,255,255,.6)' : '#fff',
                }"
              >
                {{ row.label }}
              </span>
              <span
                class="min-w-0 flex-1 truncate text-[10px] font-medium uppercase leading-[13px] tracking-[0.04em] text-white/[0.58] tabular-nums"
              >
                {{ row.xp }}
              </span>
              <span
                class="flex flex-none items-center gap-1 whitespace-nowrap text-[9.5px] font-bold uppercase leading-3 tracking-[0.08em]"
                :style="{ color: row.statusFg }"
              >
                {{ row.status }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </button>
  </div>
</template>

<style scoped lang="postcss">
@keyframes levelsNeonBreath {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}

.levels-neon {
  animation: levelsNeonBreath 2.4s ease-in-out infinite;
}
</style>
